/**
 * FactoryCommandCenter — iter226 Part 1
 *
 * Digital whiteboard for the Factory Head. Rolling 7-day view (today
 * + next 6 days), drag-and-drop between days, per-plan notes + issues
 * + change requests, full audit history, and role-scoped views.
 *
 * Backend: /app/backend/routes/factory_plans.py
 * Roles used from the existing users.role field:
 *   owner + admin           → Factory Head
 *   manager                 → Production Manager
 *   staff / production_supervisor → Supervisor
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, Calendar, Filter, GridIcon, LayoutGrid, ChevronLeft, ChevronRight,
  AlertTriangle, MessageSquare, RotateCw, ArrowRight, Search, ArrowLeftRight,
  History as HistoryIcon, GitBranch, Send, Sparkles, ClipboardList, CalendarDays,
  ShieldCheck,
} from 'lucide-react';
import { fmtQty } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import AddProductionModal from '../components/factory/AddProductionModal';
import OrderBookGrid from '../components/factory/OrderBookGrid';
import DailyProductionGrid from '../components/factory/DailyProductionGrid';
import PlanningWorkspace from '../components/factory/PlanningWorkspace';
import FactoryRules from '../components/factory/FactoryRules';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Constants ─────────────────────────────────────────────────────────

const PRIORITY_META = {
  critical: { label: 'Critical', color: 'bg-red-500/20 text-red-300 border-red-500/40', dot: 'bg-red-500', order: 0 },
  high:     { label: 'High',     color: 'bg-orange-500/20 text-orange-300 border-orange-500/40', dot: 'bg-orange-500', order: 1 },
  normal:   { label: 'Normal',   color: 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40', dot: 'bg-zinc-500', order: 2 },
  low:      { label: 'Low',      color: 'bg-zinc-800/40 text-zinc-500 border-zinc-700/40', dot: 'bg-zinc-600', order: 3 },
};

const STATUS_META = {
  planning: { label: 'Planning', color: 'text-zinc-400' },
  planned: { label: 'Planned', color: 'text-blue-400' },
  assigned: { label: 'Assigned', color: 'text-indigo-400' },
  accepted: { label: 'Accepted', color: 'text-cyan-400' },
  in_progress: { label: 'In Progress', color: 'text-amber-400' },
  completed: { label: 'Completed', color: 'text-emerald-400' },
  partially_completed: { label: 'Partial', color: 'text-yellow-400' },
  delayed: { label: 'Delayed', color: 'text-red-400' },
  cancelled: { label: 'Cancelled', color: 'text-zinc-500 line-through' },
};

const PURPOSES = [
  { v: 'customer_order', l: 'Customer Order' },
  { v: 'stock_replenishment', l: 'Stock Replenishment' },
  { v: 'buffer_stock', l: 'Buffer Stock' },
  { v: 'forecast', l: 'Forecast / Expected Demand' },
  { v: 'management_decision', l: 'Management Decision' },
  { v: 'other', l: 'Other' },
];

const ISSUE_CATEGORIES = [
  { v: 'raw_material', l: 'Raw Material' },
  { v: 'machine', l: 'Machine' },
  { v: 'manpower', l: 'Manpower' },
  { v: 'quality', l: 'Quality' },
  { v: 'previous_production_pending', l: 'Previous Production Pending' },
  { v: 'production_delay', l: 'Production Delay' },
  { v: 'quantity_issue', l: 'Quantity Issue' },
  { v: 'timing_issue', l: 'Timing Issue' },
  { v: 'other', l: 'Other' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
};

const countdownLabel = (isoDate, today) => {
  const d = new Date(isoDate + 'T00:00:00');
  const t = new Date(today + 'T00:00:00');
  const diff = Math.round((d - t) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'TODAY';
  if (diff === 1) return '1 day to go';
  if (diff > 1) return `${diff} days to go`;
  if (diff === -1) return 'Yesterday';
  return `${Math.abs(diff)} days ago`;
};

// ─── Main page ─────────────────────────────────────────────────────────

export default function FactoryCommandCenter() {
  const { user } = useAuth();
  const isFactoryHead = user && ['owner', 'admin'].includes(user.role);
  const isManager = user && ['owner', 'admin', 'manager'].includes(user.role);

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // selected plan for drawer
  const [showCreate, setShowCreate] = useState(false);
  const [showAIAdd, setShowAIAdd] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'order_book' | 'daily_grid' | 'matrix'
  const [dragging, setDragging] = useState(null);
  const [pendingMoveTarget, setPendingMoveTarget] = useState(null);
  const [filters, setFilters] = useState({ priority: '', status: '', purpose: '', q: '' });
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/factory-plans/rolling-7d`, { withCredentials: true });
      setBoard(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load command center');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchBoard();
    axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }).then(r => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API}/clients/`, { withCredentials: true }).then(r => setClients((r.data || []).filter(c => c.type === 'Debtor' || c.type === 'Both'))).catch(() => {});
    axios.get(`${API}/users/`, { withCredentials: true }).then(r => setUsers(r.data || [])).catch(() => {});
  }, [fetchBoard]);

  // Filter plans in-memory
  const filteredBoard = useMemo(() => {
    if (!board) return null;
    const p = (plans) => plans.filter(pl => {
      if (filters.priority && pl.priority !== filters.priority) return false;
      if (filters.status && pl.status !== filters.status) return false;
      if (filters.purpose && pl.purpose !== filters.purpose) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        return (pl.product_name || '').toLowerCase().includes(q) ||
               (pl.customer_name || '').toLowerCase().includes(q) ||
               (pl.id || '').toLowerCase().includes(q);
      }
      return true;
    });
    return {
      ...board,
      days: board.days.map(d => ({ ...d, plans: p(d.plans || []) })),
    };
  }, [board, filters]);

  const onDragStart = (plan) => setDragging(plan);
  const onDragOver = (e, date) => { e.preventDefault(); setPendingMoveTarget(date); };
  const onDrop = async (e, targetDate) => {
    e.preventDefault();
    if (!dragging || dragging.planned_date === targetDate) { setDragging(null); setPendingMoveTarget(null); return; }
    if (!isManager) { toast.error('Only Factory Head / Manager can move plans'); return; }
    const confirmed = window.confirm(`Move "${dragging.product_name}" (${fmtQty(dragging.quantity, dragging.product_unit)}) from ${fmtDate(dragging.planned_date)} → ${fmtDate(targetDate)}?`);
    if (!confirmed) { setDragging(null); setPendingMoveTarget(null); return; }
    try {
      await axios.post(`${API}/factory-plans/${dragging.id}/move`, { planned_date: targetDate }, { withCredentials: true });
      toast.success(`Moved to ${fmtDate(targetDate)}`);
      fetchBoard();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Move failed');
    } finally { setDragging(null); setPendingMoveTarget(null); }
  };

  if (loading) return <div className="p-6 text-zinc-500">Loading Factory Command Center…</div>;
  if (!filteredBoard) return null;

  const topSummary = board.top_summary;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <span className="text-amber-400">Tirth</span> Factory Command Center
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Rolling 7-day production board · {board.today && `Today is ${fmtDate(board.today)}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-zinc-800 border border-zinc-700 rounded-xl p-0.5">
            <button data-testid="fp-view-board" onClick={() => setViewMode('board')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'board' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <LayoutGrid size={12} /> Rolling 7-Day
            </button>
            <button data-testid="fp-view-planning-overview" onClick={() => setViewMode('planning_overview')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'planning_overview' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <ClipboardList size={12} /> Planning Overview
            </button>
            <button data-testid="fp-view-order-book" onClick={() => setViewMode('order_book')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'order_book' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <ClipboardList size={12} /> Order Book
            </button>
            <button data-testid="fp-view-daily-grid" onClick={() => setViewMode('daily_grid')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'daily_grid' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <CalendarDays size={12} /> Daily Grid
            </button>
            <button data-testid="fp-view-matrix" onClick={() => setViewMode('matrix')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'matrix' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <GridIcon size={12} /> Matrix
            </button>
            <button data-testid="fp-view-rules" onClick={() => setViewMode('rules')} className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 ${viewMode === 'rules' ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <ShieldCheck size={12} /> Rules
            </button>
          </div>
          <button data-testid="fp-history-btn" onClick={() => setShowHistory(true)}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold px-3 py-1.5 rounded-xl border border-zinc-700">
            <Calendar size={13} /> History
          </button>
          {isManager && (
            <>
              <button data-testid="fp-ai-add-btn" onClick={() => setShowAIAdd(true)}
                className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold text-xs px-3 py-1.5 rounded-xl">
                <Sparkles size={13} /> AI Add
              </button>
              <button data-testid="fp-create-plan-btn" onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-xl">
                <Plus size={13} /> Manual Add
              </button>
            </>
          )}
          <button onClick={fetchBoard}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-xs font-semibold px-2.5 py-1.5 rounded-xl border border-zinc-700"
            title="Refresh">
            <RotateCw size={13} />
          </button>
        </div>
      </div>

      {/* Top summary card */}
      <TopSummaryCard summary={topSummary} today={board.today} />

      {/* Filter row — only for board/matrix views */}
      {(viewMode === 'board' || viewMode === 'matrix') && (
        <div className="flex items-center gap-2 flex-wrap bg-zinc-900/40 border border-zinc-800 rounded-xl p-2">          <div className="flex items-center gap-1.5 flex-1 min-w-[200px] bg-zinc-800 rounded-lg px-2.5 py-1.5">
            <Search size={12} className="text-zinc-500" />
            <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search product / customer / FP-ID…" data-testid="fp-search"
              className="w-full bg-transparent text-xs text-zinc-100 focus:outline-none" />
          </div>
          <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })} data-testid="fp-filter-priority"
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-300">
            <option value="">All priorities</option>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="fp-filter-status"
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-300">
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.purpose} onChange={e => setFilters({ ...filters, purpose: e.target.value })} data-testid="fp-filter-purpose"
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-300">
            <option value="">All purposes</option>
            {PURPOSES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </div>
      )}

      {/* Main content */}
      {viewMode === 'board' && (
        <BoardView
          days={filteredBoard.days}
          today={board.today}
          onCardClick={setSelected}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          pendingMoveTarget={pendingMoveTarget}
          isManager={isManager}
        />
      )}
      {viewMode === 'order_book' && <OrderBookGrid />}
      {viewMode === 'daily_grid' && <DailyProductionGrid />}
      {viewMode === 'planning_overview' && <PlanningWorkspace />}
      {viewMode === 'rules' && <FactoryRules />}
      {viewMode === 'matrix' && (
        <MatrixView start={board.days[0].date} end={board.days[6].date} onPlanClick={(id) => {
          const plan = board.days.flatMap(d => d.plans).find(p => p.id === id);
          if (plan) setSelected(plan);
        }} />
      )}

      {/* Modals */}
      {showAIAdd && (
        <AddProductionModal
          products={products}
          clients={clients}
          defaultDate={board.today}
          onClose={() => setShowAIAdd(false)}
          onCreated={() => { setShowAIAdd(false); fetchBoard(); }}
        />
      )}
      {showCreate && (
        <CreatePlanModal
          products={products}
          clients={clients}
          users={users}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchBoard(); }}
          defaultDate={board.today}
        />
      )}
      {selected && (
        <PlanDetailDrawer
          plan={selected}
          user={user}
          isFactoryHead={isFactoryHead}
          isManager={isManager}
          onClose={() => setSelected(null)}
          onRefresh={async () => {
            await fetchBoard();
            const fresh = await axios.get(`${API}/factory-plans/${selected.id}`, { withCredentials: true });
            setSelected(fresh.data);
          }}
        />
      )}
      {showHistory && (
        <HistoryModal onClose={() => setShowHistory(false)} onPlanClick={setSelected} />
      )}
    </div>
  );
}

// ─── Top summary card ─────────────────────────────────────────────────

function TopSummaryCard({ summary, today }) {
  if (!summary) return null;
  const items = [
    { l: 'Production Plans', v: summary.plans, tone: 'text-zinc-100' },
    { l: 'Batches', v: summary.batches, tone: 'text-zinc-100' },
    { l: 'Quantity', v: fmtQty(summary.quantity, 'kg'), tone: 'text-amber-400' },
    { l: 'Critical', v: summary.critical, tone: summary.critical > 0 ? 'text-red-400' : 'text-zinc-400' },
    { l: 'High', v: summary.high, tone: summary.high > 0 ? 'text-orange-400' : 'text-zinc-400' },
    { l: 'Delayed', v: summary.delayed, tone: summary.delayed > 0 ? 'text-red-400' : 'text-zinc-400' },
    { l: 'Open Issues', v: summary.open_issues, tone: summary.open_issues > 0 ? 'text-yellow-400' : 'text-zinc-400' },
    { l: 'Carry Forward', v: summary.carry_forward, tone: summary.carry_forward > 0 ? 'text-amber-400' : 'text-zinc-400' },
  ];
  return (
    <div data-testid="fp-top-summary" className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-amber-700/30 rounded-2xl p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-400/70">Next 7 days</p>
          <p className="text-zinc-500 text-xs mt-0.5">
            {today && `Starting ${fmtDate(today)}`}
            {summary.cancelled > 0 && (
              <span className="text-zinc-600" data-testid="fp-top-cancelled">
                {' · '}{summary.cancelled} cancelled slot(s) not counted
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
        {items.map(i => (
          <div key={i.l}>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{i.l}</p>
            <p className={`font-heading text-xl font-bold ${i.tone}`}>{i.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Board view ────────────────────────────────────────────────────────

function BoardView({ days, today, onCardClick, onDragStart, onDragOver, onDrop, pendingMoveTarget, isManager }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
      {days.map(d => (
        <DayColumn
          key={d.date}
          day={d}
          today={today}
          onCardClick={onCardClick}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          isDropTarget={pendingMoveTarget === d.date}
          isManager={isManager}
        />
      ))}
    </div>
  );
}

function DayColumn({ day, today, onCardClick, onDragStart, onDragOver, onDrop, isDropTarget, isManager }) {
  const isToday = day.is_today;
  return (
    <div
      data-testid={`fp-day-column-${day.date}`}
      onDragOver={e => isManager && onDragOver(e, day.date)}
      onDrop={e => isManager && onDrop(e, day.date)}
      className={`rounded-2xl border ${isToday ? 'border-amber-500/70 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/40'} ${isDropTarget ? 'ring-2 ring-amber-400/60' : ''} min-h-[240px] flex flex-col`}>
      <div className={`px-3 py-2 border-b ${isToday ? 'border-amber-500/40' : 'border-zinc-800'}`}>
        <div className="flex items-baseline justify-between">
          <div>
            <p className={`font-heading text-sm font-bold ${isToday ? 'text-amber-300' : 'text-zinc-200'}`}>
              {day.day_of_week.slice(0, 3).toUpperCase()} · {day.date.slice(-2)} {new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' })}
            </p>
            {isToday && <p className="text-[9px] uppercase tracking-widest text-amber-500 font-bold mt-0.5">TODAY</p>}
          </div>
          <span className="text-[10px] text-zinc-500">{day.summary.plans}p / {day.summary.batches}b</span>
        </div>
        {day.summary.quantity > 0 && (
          <p className="text-[10px] text-zinc-500 mt-1">
            <span className="text-zinc-300">{fmtQty(day.summary.quantity)}</span> {day.summary.critical > 0 && <span className="text-red-400 ml-1">🔴 {day.summary.critical}</span>}
            {day.summary.high > 0 && <span className="text-orange-400 ml-1">🟠 {day.summary.high}</span>}
            {day.summary.open_issues > 0 && <span className="text-yellow-400 ml-1">⚠️ {day.summary.open_issues}</span>}
            {day.summary.delayed > 0 && <span className="text-red-400 ml-1">⏰ {day.summary.delayed}</span>}
          </p>
        )}
      </div>
      <div className="flex-1 p-2 space-y-2 overflow-auto">
        {day.plans.length === 0 && (
          <div className="text-center py-6 text-zinc-600 text-[11px]">No plans</div>
        )}
        {day.plans.map(p => (
          <PlanCard key={p.id} plan={p} today={today} onClick={() => onCardClick(p)} onDragStart={() => onDragStart(p)} isManager={isManager} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, today, onClick, onDragStart, isManager }) {
  const pmeta = PRIORITY_META[plan.priority] || PRIORITY_META.normal;
  const smeta = STATUS_META[plan.status] || STATUS_META.planned;
  const openIssues = (plan.issues || []).filter(i => !i.resolved).length;
  const openCRs = (plan.change_requests || []).filter(c => c.status === 'pending').length;
  const notesCount = (plan.notes || []).length;
  return (
    <div
      data-testid={`fp-plan-card-${plan.id}`}
      draggable={isManager}
      onDragStart={onDragStart}
      onClick={onClick}
      className={`rounded-xl border ${pmeta.color} p-2.5 cursor-pointer hover:brightness-125 transition-all ${isManager ? 'active:opacity-70' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm font-semibold truncate text-zinc-50">{plan.product_name || '(Unnamed)'}</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">{plan.id} · {countdownLabel(plan.planned_date, today)}</p>
        </div>
        <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${pmeta.color}`}>{pmeta.label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="text-amber-400 font-mono font-semibold text-sm">{fmtQty(plan.quantity, plan.product_unit)}</span>
        {plan.batches > 0 && <span className="text-zinc-500 text-[10px]">{plan.batches} batches</span>}
      </div>
      {plan.customer_name && (
        <p className="text-[10px] text-zinc-400 mt-1 truncate">For: <span className="text-zinc-200">{plan.customer_name}</span></p>
      )}
      {plan.production_supervisor_name && (
        <p className="text-[10px] text-zinc-500 mt-0.5 truncate">Sup: {plan.production_supervisor_name}</p>
      )}
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className={smeta.color}>{smeta.label}</span>
        <div className="flex items-center gap-2 text-zinc-500">
          {notesCount > 0 && <span title="Notes" className="flex items-center gap-0.5"><MessageSquare size={9}/> {notesCount}</span>}
          {openIssues > 0 && <span title="Open issues" className="flex items-center gap-0.5 text-yellow-400"><AlertTriangle size={9}/> {openIssues}</span>}
          {openCRs > 0 && <span title="Pending change requests" className="flex items-center gap-0.5 text-purple-400"><GitBranch size={9}/> {openCRs}</span>}
          {plan.carry_forward_from && <span title="Carried forward" className="text-amber-400">↪</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Matrix view ───────────────────────────────────────────────────────

function MatrixView({ start, end, onPlanClick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/factory-plans/matrix`, { params: { start, end }, withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData({ rows: [], dates: [] }));
  }, [start, end]);
  if (!data) return <div className="text-zinc-500 p-6 text-sm">Loading matrix…</div>;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl overflow-auto">
      <table className="w-full text-xs" data-testid="fp-matrix-table">
        <thead className="bg-zinc-900 sticky top-0">
          <tr>
            <th className="px-3 py-2 text-left text-zinc-400 font-semibold uppercase text-[10px]">Product</th>
            {data.dates.map(d => (
              <th key={d} className="px-3 py-2 text-center text-zinc-400 font-semibold uppercase text-[10px]">
                {new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr><td colSpan={data.dates.length + 1} className="text-center py-10 text-zinc-600">No plans in this window.</td></tr>
          )}
          {data.rows.map(row => (
            <tr key={row.product} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
              <td className="px-3 py-2 text-zinc-100 font-medium">{row.product}</td>
              {data.dates.map(d => {
                const cell = row.cells[d];
                return (
                  <td key={d} className="px-3 py-2 text-center">
                    {cell ? (
                      <button onClick={() => cell.plans[0] && onPlanClick(cell.plans[0].id)}
                        className="text-amber-400 font-mono text-xs hover:underline">
                        {fmtQty(cell.qty)} <span className="text-zinc-500 text-[9px]">({cell.batches}b)</span>
                      </button>
                    ) : <span className="text-zinc-700">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Create modal ──────────────────────────────────────────────────────

function CreatePlanModal({ products, clients, users, onClose, onCreated, defaultDate }) {
  const [form, setForm] = useState({
    product_id: '', product_name: '',
    planned_date: defaultDate,
    quantity: '', batches: '',
    customer_id: '', customer_name: '',
    sales_order_id: '', sales_order_number: '',
    purpose: 'stock_replenishment',
    priority: 'normal',
    production_manager_id: '', production_manager_name: '',
    production_supervisor_id: '', production_supervisor_name: '',
    instructions: '',
  });
  const [saving, setSaving] = useState(false);

  const productSuggestions = useMemo(() => {
    const q = form.product_name.toLowerCase();
    if (!q) return [];
    return products.filter(p => p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [products, form.product_name]);

  const managers = users.filter(u => ['manager', 'owner', 'admin'].includes(u.role));
  const supervisors = users.filter(u => ['staff', 'manager', 'production_supervisor', 'owner', 'admin'].includes(u.role));

  const save = async () => {
    if (!form.product_name.trim()) { toast.error('Product name required'); return; }
    if (!form.planned_date) { toast.error('Planned date required'); return; }
    if (!form.quantity && !form.batches) { toast.error('Quantity or batches required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/factory-plans/`, {
        ...form,
        quantity: form.quantity ? Number(form.quantity) : 0,
        batches: form.batches ? Number(form.batches) : 0,
      }, { withCredentials: true });
      toast.success('Production plan created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <h2 className="font-heading text-lg text-zinc-50">+ Add Production Plan</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {/* Product */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Product / Recipe</label>
            <input value={form.product_name} onChange={e => setForm({ ...form, product_name: e.target.value, product_id: '' })}
              placeholder="Type or pick a product…" data-testid="fp-create-product"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
            {productSuggestions.length > 0 && (
              <div className="mt-1 bg-zinc-900 border border-zinc-700 rounded-lg max-h-40 overflow-auto">
                {productSuggestions.map(p => (
                  <button key={p.id} onClick={() => setForm({ ...form, product_name: p.name, product_id: p.id })}
                    className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800">
                    {p.name} <span className="text-zinc-500 text-[10px]">· {p.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Planned Date</label>
              <input type="date" value={form.planned_date} onChange={e => setForm({ ...form, planned_date: e.target.value })}
                data-testid="fp-create-date"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                data-testid="fp-create-priority"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Quantity</label>
              <input type="number" step="any" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })}
                data-testid="fp-create-quantity"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Batches</label>
              <input type="number" value={form.batches} onChange={e => setForm({ ...form, batches: e.target.value })}
                data-testid="fp-create-batches"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Purpose</label>
            <select value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })}
              data-testid="fp-create-purpose"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm">
              {PURPOSES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Customer (optional)</label>
            <select value={form.customer_id} onChange={e => {
              const c = clients.find(x => x.id === e.target.value);
              setForm({ ...form, customer_id: e.target.value, customer_name: c ? c.name : '' });
            }} data-testid="fp-create-customer"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm">
              <option value="">— Not assigned —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Production Manager</label>
              <select value={form.production_manager_id} onChange={e => {
                const u = managers.find(x => x.id === e.target.value);
                setForm({ ...form, production_manager_id: e.target.value, production_manager_name: u ? u.name : '' });
              }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                <option value="">— Select —</option>
                {managers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Supervisor</label>
              <select value={form.production_supervisor_id} onChange={e => {
                const u = supervisors.find(x => x.id === e.target.value);
                setForm({ ...form, production_supervisor_id: e.target.value, production_supervisor_name: u ? u.name : '' });
              }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                <option value="">— Select —</option>
                {supervisors.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Instructions</label>
            <textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
              rows={2} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm resize-none" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-zinc-400 hover:text-zinc-200 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="fp-create-save"
            className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold text-sm rounded-xl">
            {saving ? 'Saving…' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail drawer ─────────────────────────────────────────────────────

function PlanDetailDrawer({ plan, user, isFactoryHead, isManager, onClose, onRefresh }) {
  const [tab, setTab] = useState('overview');
  const [noteText, setNoteText] = useState('');
  const [issueForm, setIssueForm] = useState({ category: 'raw_material', description: '' });
  const [crForm, setCrForm] = useState({ planned_date: plan.planned_date, quantity: plan.quantity, priority: plan.priority, reason: '' });
  const [statusForm, setStatusForm] = useState({ status: plan.status, completed_quantity: plan.completed_quantity || '' });
  const [carryDate, setCarryDate] = useState('');

  const openIssues = (plan.issues || []).filter(i => !i.resolved);
  const pendingCRs = (plan.change_requests || []).filter(c => c.status === 'pending');

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/factory-plans/${plan.id}/notes`, { message: noteText }, { withCredentials: true });
      setNoteText('');
      onRefresh();
      toast.success('Note added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reportIssue = async () => {
    if (!issueForm.description.trim()) { toast.error('Describe the issue'); return; }
    try {
      await axios.post(`${API}/factory-plans/${plan.id}/issues`, issueForm, { withCredentials: true });
      setIssueForm({ category: 'raw_material', description: '' });
      onRefresh();
      toast.success('Issue reported');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resolveIssue = async (issueId) => {
    try {
      await axios.patch(`${API}/factory-plans/${plan.id}/issues/${issueId}`, { resolved: true }, { withCredentials: true });
      onRefresh();
      toast.success('Issue resolved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const requestChange = async () => {
    const requested = {};
    if (crForm.planned_date !== plan.planned_date) requested.planned_date = crForm.planned_date;
    if (Number(crForm.quantity) !== plan.quantity) requested.quantity = Number(crForm.quantity);
    if (crForm.priority !== plan.priority) requested.priority = crForm.priority;
    if (Object.keys(requested).length === 0) { toast.error('Nothing changed to request'); return; }
    try {
      await axios.post(`${API}/factory-plans/${plan.id}/change-requests`, { requested, reason: crForm.reason }, { withCredentials: true });
      onRefresh();
      toast.success('Change request submitted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const decideCR = async (crId, decision) => {
    const note = decision === 'rejected' ? window.prompt('Rejection reason?') || '' : '';
    try {
      await axios.patch(`${API}/factory-plans/${plan.id}/change-requests/${crId}`, { decision, decision_note: note }, { withCredentials: true });
      onRefresh();
      toast.success(`Change ${decision}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const updateStatus = async () => {
    try {
      await axios.post(`${API}/factory-plans/${plan.id}/status`, {
        status: statusForm.status,
        completed_quantity: statusForm.completed_quantity ? Number(statusForm.completed_quantity) : 0,
      }, { withCredentials: true });
      onRefresh();
      toast.success('Status updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const carryFwd = async () => {
    if (!carryDate) { toast.error('Pick a date'); return; }
    try {
      await axios.post(`${API}/factory-plans/${plan.id}/carry-forward`, { new_date: carryDate }, { withCredentials: true });
      onRefresh();
      toast.success('Carried forward');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const cancelPlan = async () => {
    if (!window.confirm('Cancel this production plan?')) return;
    try {
      await axios.delete(`${API}/factory-plans/${plan.id}`, { withCredentials: true });
      onRefresh();
      onClose();
      toast.success('Plan cancelled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pmeta = PRIORITY_META[plan.priority] || PRIORITY_META.normal;
  const smeta = STATUS_META[plan.status] || STATUS_META.planned;

  const tabs = [
    { k: 'overview', l: 'Overview' },
    { k: 'notes', l: `Notes (${(plan.notes || []).length})` },
    { k: 'issues', l: `Issues (${openIssues.length})` },
    { k: 'changes', l: `Change Requests (${pendingCRs.length})` },
    { k: 'history', l: 'History' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end">
      <div className="bg-zinc-900 border-l border-zinc-800 w-full max-w-2xl h-full overflow-auto" data-testid={`fp-drawer-${plan.id}`}>
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-5 py-3 z-10">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] text-zinc-500">{plan.id} · {plan.planned_date}</p>
              <h2 className="font-heading text-lg text-zinc-50 mt-0.5">{plan.product_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${pmeta.color}`}>{pmeta.label}</span>
                <span className={`text-xs ${smeta.color}`}>{smeta.label}</span>
                {plan.carry_forward_from && <span className="text-[10px] text-amber-400">↪ {plan.carry_forward_from}</span>}
              </div>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
          </div>
          <div className="flex items-center gap-1 mt-3 overflow-auto">
            {tabs.map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-3 py-1 text-xs rounded-lg whitespace-nowrap ${tab === t.k ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:bg-zinc-800'}`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {tab === 'overview' && (
            <div className="space-y-4">
              <Row l="Quantity" v={<span className="text-amber-400 font-mono font-semibold">{fmtQty(plan.quantity, plan.product_unit)}</span>} />
              <Row l="Batches" v={plan.batches} />
              <Row l="Completed" v={<span className="text-emerald-400">{fmtQty(plan.completed_quantity)}</span>} />
              <Row l="Remaining" v={<span className="text-yellow-400">{fmtQty(plan.remaining_quantity)}</span>} />
              <Row l="Customer" v={plan.customer_name || <span className="text-zinc-600">— Not assigned —</span>} />
              <Row l="Sales Order" v={plan.sales_order_number || <span className="text-zinc-600">—</span>} />
              <Row l="Purpose" v={PURPOSES.find(p => p.v === plan.purpose)?.l || plan.purpose} />
              <Row l="Manager" v={plan.production_manager_name || <span className="text-zinc-600">—</span>} />
              <Row l="Supervisor" v={plan.production_supervisor_name || <span className="text-zinc-600">—</span>} />
              <Row l="Instructions" v={plan.instructions || <span className="text-zinc-600">—</span>} />

              {/* Reserved intelligence area — Part 2 */}
              <div className="mt-4 p-3 rounded-xl border border-dashed border-zinc-700 bg-zinc-900/30">
                <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-2">Intelligence (Part 2)</p>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div><span className="text-zinc-500">RM Status</span><span className="ml-1 text-zinc-600">·</span></div>
                  <div><span className="text-zinc-500">FG Status</span><span className="ml-1 text-zinc-600">·</span></div>
                  <div><span className="text-zinc-500">Order Coverage</span><span className="ml-1 text-zinc-600">·</span></div>
                </div>
              </div>

              {/* Status update */}
              <div className="border-t border-zinc-800 pt-4 space-y-2">
                <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Update Status</p>
                <div className="flex items-center gap-2">
                  <select value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100">
                    {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <input type="number" placeholder="Completed qty" value={statusForm.completed_quantity}
                    onChange={e => setStatusForm({ ...statusForm, completed_quantity: e.target.value })}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
                  <button data-testid="fp-update-status-btn" onClick={updateStatus} className="bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold px-3 py-1.5 rounded-lg">Save</button>
                </div>
              </div>

              {/* Carry forward */}
              {plan.remaining_quantity > 0 && isManager && (
                <div className="border-t border-zinc-800 pt-4 space-y-2">
                  <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Carry Forward Remaining ({fmtQty(plan.remaining_quantity, plan.product_unit)})</p>
                  <div className="flex items-center gap-2">
                    <input type="date" value={carryDate} onChange={e => setCarryDate(e.target.value)}
                      data-testid="fp-carry-forward-date"
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
                    <button data-testid="fp-carry-forward-btn" onClick={carryFwd} className="bg-purple-500 hover:bg-purple-400 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Carry Forward</button>
                  </div>
                </div>
              )}

              {isFactoryHead && plan.status !== 'cancelled' && (
                <div className="border-t border-zinc-800 pt-4">
                  <button onClick={cancelPlan} className="text-red-400 text-xs hover:text-red-300">Cancel this plan</button>
                </div>
              )}
            </div>
          )}
          {tab === 'notes' && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[50vh] overflow-auto">
                {(plan.notes || []).length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No notes yet.</p>}
                {(plan.notes || []).slice().reverse().map(n => (
                  <div key={n.id} className="bg-zinc-800/50 rounded-lg p-2.5 border-l-2 border-amber-500/40">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] text-zinc-300 font-semibold">{n.user_name} <span className="text-zinc-500 font-normal">· {n.role}</span></p>
                      <p className="text-[10px] text-zinc-500">{new Date(n.at).toLocaleString('en-IN')}</p>
                    </div>
                    <p className="text-xs text-zinc-200 whitespace-pre-wrap">{n.message}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-zinc-800 pt-3 flex items-start gap-2">
                <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2}
                  data-testid="fp-note-input"
                  placeholder="Add a note visible to the whole team…"
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-amber-500 resize-none" />
                <button data-testid="fp-add-note-btn" onClick={addNote}
                  className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-2 rounded-lg text-xs">
                  <Send size={13} />
                </button>
              </div>
            </div>
          )}
          {tab === 'issues' && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[45vh] overflow-auto">
                {(plan.issues || []).length === 0 && <p className="text-zinc-600 text-sm text-center py-6">No issues reported.</p>}
                {(plan.issues || []).slice().reverse().map(i => (
                  <div key={i.id} className={`rounded-lg p-2.5 ${i.resolved ? 'bg-zinc-800/30 opacity-60' : 'bg-yellow-900/20 border border-yellow-800/40'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-[11px] font-semibold ${i.resolved ? 'text-zinc-400 line-through' : 'text-yellow-300'}`}>
                        {ISSUE_CATEGORIES.find(c => c.v === i.category)?.l || i.category}
                      </p>
                      <p className="text-[10px] text-zinc-500">{new Date(i.at).toLocaleString('en-IN')}</p>
                    </div>
                    <p className="text-xs text-zinc-200">{i.description}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">by {i.reported_by} · {i.reported_by_role}</p>
                    {!i.resolved && isManager && (
                      <button onClick={() => resolveIssue(i.id)} className="mt-1 text-[10px] text-emerald-400 hover:text-emerald-300">Mark resolved</button>
                    )}
                    {i.resolved && <p className="text-[10px] text-emerald-500 mt-1">✓ Resolved by {i.resolved_by}</p>}
                  </div>
                ))}
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-2">
                <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Report Issue</p>
                <select value={issueForm.category} onChange={e => setIssueForm({ ...issueForm, category: e.target.value })}
                  data-testid="fp-issue-category"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100">
                  {ISSUE_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
                <textarea value={issueForm.description} onChange={e => setIssueForm({ ...issueForm, description: e.target.value })}
                  data-testid="fp-issue-description"
                  placeholder="Describe the issue…" rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 resize-none" />
                <button onClick={reportIssue} data-testid="fp-report-issue-btn"
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-1.5 rounded-lg text-xs">Report Issue</button>
              </div>
            </div>
          )}
          {tab === 'changes' && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[40vh] overflow-auto">
                {(plan.change_requests || []).length === 0 && <p className="text-zinc-600 text-sm text-center py-6">No change requests.</p>}
                {(plan.change_requests || []).slice().reverse().map(cr => (
                  <div key={cr.id} className={`rounded-lg p-2.5 ${cr.status === 'pending' ? 'bg-purple-900/20 border border-purple-800/40' : cr.status === 'approved' ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-red-900/20 border border-red-800/40'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] font-semibold text-zinc-100">
                        {cr.status === 'pending' ? '⏳' : cr.status === 'approved' ? '✅' : '❌'} {cr.status.toUpperCase()}
                      </p>
                      <p className="text-[10px] text-zinc-500">{new Date(cr.requested_at).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-xs text-zinc-200 space-y-1">
                      {Object.entries(cr.requested || {}).map(([k, v]) => (
                        <p key={k}><span className="text-zinc-500">{k}:</span> <span className="text-zinc-500">{JSON.stringify(cr.current?.[k])}</span> → <span className="text-amber-400">{JSON.stringify(v)}</span></p>
                      ))}
                    </div>
                    {cr.reason && <p className="text-[11px] text-zinc-400 mt-1 italic">&ldquo;{cr.reason}&rdquo;</p>}
                    <p className="text-[10px] text-zinc-500 mt-1">by {cr.requested_by}</p>
                    {cr.status === 'pending' && isFactoryHead && (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => decideCR(cr.id, 'approved')} data-testid={`fp-approve-cr-${cr.id}`}
                          className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-[11px] py-1 rounded">Approve</button>
                        <button onClick={() => decideCR(cr.id, 'rejected')}
                          className="flex-1 bg-red-500 hover:bg-red-400 text-white font-semibold text-[11px] py-1 rounded">Reject</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-2">
                <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Request Change</p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={crForm.planned_date} onChange={e => setCrForm({ ...crForm, planned_date: e.target.value })}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
                  <input type="number" step="any" value={crForm.quantity} onChange={e => setCrForm({ ...crForm, quantity: e.target.value })}
                    placeholder="Qty" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
                </div>
                <select value={crForm.priority} onChange={e => setCrForm({ ...crForm, priority: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100">
                  {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <textarea value={crForm.reason} onChange={e => setCrForm({ ...crForm, reason: e.target.value })}
                  placeholder="Reason for change…" rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 resize-none" />
                <button onClick={requestChange} data-testid="fp-request-change-btn"
                  className="w-full bg-purple-500 hover:bg-purple-400 text-white font-semibold py-1.5 rounded-lg text-xs">Request Change</button>
              </div>
            </div>
          )}
          {tab === 'history' && (
            <div className="space-y-2 max-h-[70vh] overflow-auto">
              {(plan.history || []).slice().reverse().map((h, idx) => (
                <div key={idx} className="border-l-2 border-zinc-700 pl-3 pb-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold text-amber-400">{h.action}</p>
                    <p className="text-[10px] text-zinc-500">{new Date(h.at).toLocaleString('en-IN')}</p>
                  </div>
                  <p className="text-[10px] text-zinc-500">by {h.by} ({h.by_role})</p>
                  {h.before && <p className="text-[10px] text-zinc-500 mt-1">Before: <span className="text-zinc-400">{JSON.stringify(h.before)}</span></p>}
                  {h.after && <p className="text-[10px] text-emerald-500 mt-0.5">After: <span className="text-emerald-400">{JSON.stringify(h.after)}</span></p>}
                  {h.note && <p className="text-[10px] text-zinc-400 mt-0.5 italic">&ldquo;{h.note}&rdquo;</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{l}</p>
      <p className="col-span-2 text-sm text-zinc-100">{v}</p>
    </div>
  );
}

// ─── History modal ─────────────────────────────────────────────────────

function HistoryModal({ onClose, onPlanClick }) {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/factory-plans/`, {
        params: { date_from: dateFrom, date_to: dateTo, limit: 500 },
        withCredentials: true,
      });
      setPlans(res.data || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = plans.reduce((acc, p) => {
    (acc[p.planned_date] = acc[p.planned_date] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-amber-400" />
            <h2 className="font-heading text-lg text-zinc-50">Calendar / History</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
            <span className="text-zinc-500">→</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
            <button onClick={search} data-testid="fp-history-search-btn"
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-lg">Search</button>
          </div>
          {loading && <p className="text-zinc-500 text-sm text-center py-6">Loading…</p>}
          {!loading && plans.length === 0 && <p className="text-zinc-600 text-sm text-center py-10">No plans in this range.</p>}
          <div className="space-y-4">
            {Object.keys(byDate).sort().reverse().map(d => (
              <div key={d}>
                <p className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-2">{fmtDate(d)} · {byDate[d].length} plan(s)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {byDate[d].map(p => (
                    <div key={p.id} onClick={() => onPlanClick(p)}
                      className="bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg p-2 cursor-pointer">
                      <p className="text-xs text-zinc-100 font-semibold truncate">{p.product_name}</p>
                      <p className="text-[10px] text-zinc-500">{p.id} · {fmtQty(p.quantity, p.product_unit)} · {STATUS_META[p.status]?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
